/**
 * Feedback layer (§ feel): turns gameplay EVENTS into sound + haptics.
 *
 * Platform-abstracted on purpose. Audio is Web Audio (procedurally-SYNTHESISED placeholder SFX —
 * no audio assets needed). HAPTICS now branch: inside the Capacitor native shell they use real
 * Taptic/vibration via @capacitor/haptics; on the web they fall back to the Vibration API (Android)
 * + the iOS Taptic-switch tick. (Audio could likewise move to native later — only this file changes.)
 *
 * The sim stays PURE — it never makes noise. Events are DERIVED by diffing two game
 * states ({@link deriveEvents}) in the app layer, so the deterministic sim and the
 * §5/§5b gates are untouched.
 */
import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import type { GameState, ScoreResult, Side } from '../sim/types.ts';
import { createRng } from '../sim/rng.ts';

/** True inside the iOS/Android Capacitor shell (false on the web), decided once at load. */
const NATIVE = Capacitor.isNativePlatform();

/** Map a buzz magnitude (web vibrate ms) to a Taptic impact weight. */
function impactStyle(ms: number): ImpactStyle {
  if (ms <= 12) return ImpactStyle.Light;
  if (ms <= 24) return ImpactStyle.Medium;
  return ImpactStyle.Heavy;
}

/** Native haptics: fire one impact per "on" pulse of a web vibrate pattern, timed by its gaps. */
function nativeBuzz(pattern: number | number[]): void {
  const fire = (ms: number): void => {
    void Haptics.impact({ style: impactStyle(ms) }).catch(() => {
      /* device without haptics — ignore */
    });
  };
  if (typeof pattern === 'number') {
    fire(pattern);
    return;
  }
  // [on, off, on, off, …] → an impact at each even index, scheduled by the cumulative time.
  let t = 0;
  for (let i = 0; i < pattern.length; i++) {
    const ms = pattern[i] ?? 0;
    if (i % 2 === 0) {
      if (t === 0) fire(ms);
      else setTimeout(() => fire(ms), t);
    }
    t += ms;
  }
}

/** A discrete feel moment. `mine` = the human player's own shot (louder + haptic). */
export type GameEvent =
  | { kind: 'backswingLock' }
  | { kind: 'strike'; mine: boolean }
  | { kind: 'land'; mine: boolean }
  | { kind: 'water'; mine: boolean }
  | { kind: 'scored'; result: ScoreResult; mine: boolean }
  | { kind: 'gopher'; mine: boolean };

/** The shared Web Audio nodes (context + master bus) so other layers — e.g. the music engine —
 *  can route through the SAME context, master gain and compressor as the SFX. */
export interface AudioGraph {
  readonly ctx: AudioContext;
  readonly master: GainNode;
}

export interface Feedback {
  /** Unlock audio from a user gesture (browsers block audio until then). */
  unlock(): void;
  /** Play sound + haptics for a batch of events (one tick's worth). */
  emit(events: readonly GameEvent[]): void;
  setMuted(muted: boolean): void;
  /** SFX-only volume (0..1) — independent of the music level. */
  setSfxVolume(v: number): void;
  /** Enable/disable vibration + Taptic haptics. */
  setHaptics(on: boolean): void;
  /** The shared {@link AudioGraph} (creating the context if needed), or null without Web Audio.
   *  The music engine routes into `master` so the global mute + compressor cover it too. */
  audioGraph(): AudioGraph | null;
}

// ---------------------------------------------------------------------------
// Derive events by diffing state (keeps the sim pure)
// ---------------------------------------------------------------------------

const RESULTS: readonly ScoreResult[] = ['ace', 'eagle', 'birdie', 'par', 'bogey', 'double'];

function countPhase(s: GameState, side: Side, phase: 'rolling' | 'sinking'): number {
  let n = 0;
  for (const b of s.balls) if (b.side === side && b.phase === phase) n++;
  return n;
}
function countLabel(s: GameState, side: Side, r: ScoreResult): number {
  let n = 0;
  for (const b of s.balls) if (b.side === side && b.scoreLabel === r) n++;
  return n;
}
/** Settled balls that a gopher has stolen (the steal scores no label — see physics/game). */
function countStolen(s: GameState, side: Side): number {
  let n = 0;
  for (const b of s.balls) if (b.side === side && b.stolen && b.phase === 'settled') n++;
  return n;
}

/**
 * Detect feel moments between two consecutive states — no ids needed, just counters:
 * `shots` increment = a STRIKE; a swing reaching impact = the backswing LOCK; a new
 * `rolling`/`sinking` ball = a LANDING/WATER; a new scoreLabel = a SCORED result.
 */
export function deriveEvents(prev: GameState, next: GameState): GameEvent[] {
  const out: GameEvent[] = [];

  if (next.you.shots > prev.you.shots) out.push({ kind: 'strike', mine: true });
  if (next.rival.shots > prev.rival.shots) out.push({ kind: 'strike', mine: false });

  if (prev.swing.phase === 'backswing' && next.swing.phase === 'impact' && next.swing.side === 'you') {
    out.push({ kind: 'backswingLock' });
  }

  for (const side of ['you', 'rival'] as const) {
    const mine = side === 'you';
    if (countPhase(next, side, 'rolling') > countPhase(prev, side, 'rolling')) out.push({ kind: 'land', mine });
    if (countPhase(next, side, 'sinking') > countPhase(prev, side, 'sinking')) out.push({ kind: 'water', mine });
    if (countStolen(next, side) > countStolen(prev, side)) out.push({ kind: 'gopher', mine });
    for (const r of RESULTS) {
      if (countLabel(next, side, r) > countLabel(prev, side, r)) out.push({ kind: 'scored', result: r, mine });
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Web Audio synth (placeholder SFX) + haptics
// ---------------------------------------------------------------------------

interface ToneOpts {
  type?: OscillatorType;
  gain?: number;
  sweepTo?: number; // end frequency for a glide (0 = none)
}

// Pitched SFX are snapped to the SAME key as the music (A major) so cues feel composed, not random.
// (Root must match music.ts PROG — A = MIDI 57.) Noise-based SFX have no pitch, so they're exempt.
const KEY_ROOT_MIDI = 57; // A
const KEY_SCALE = [0, 2, 4, 5, 7, 9, 11]; // major (Ionian) intervals from the root
/** Quantise a frequency to the nearest in-key pitch (A major). */
function snapToKey(freq: number): number {
  if (freq <= 0) return freq;
  const midi = 69 + 12 * Math.log2(freq / 440);
  const near = Math.round(midi);
  let best = near;
  let bestDist = Infinity;
  for (let m = near - 2; m <= near + 2; m++) {
    const pc = (((m - KEY_ROOT_MIDI) % 12) + 12) % 12;
    if (KEY_SCALE.includes(pc) && Math.abs(m - midi) < bestDist) {
      bestDist = Math.abs(m - midi);
      best = m;
    }
  }
  return 440 * 2 ** ((best - 69) / 12);
}

export function createFeedback(): Feedback {
  let ctx: AudioContext | null = null;
  let master: GainNode | null = null;
  let sfxGain: GainNode | null = null; // SFX-only sub-bus (its own volume; music bypasses it)
  let noiseBuf: AudioBuffer | null = null;
  let muted = false;
  let sfxVolume = 0.9; // 0..1, set from settings
  let hapticsEnabled = true;
  // iOS has no Vibration API; its ONLY web haptic is the Taptic "tick" fired when a
  // native <input switch> (iOS 17.4+) toggles. We keep a hidden one and flip it.
  let hapticSwitch: HTMLLabelElement | null = null;

  function ensureHaptic(): void {
    if (hapticSwitch || typeof document === 'undefined') return;
    const label = document.createElement('label');
    label.setAttribute('aria-hidden', 'true');
    label.style.cssText = 'position:fixed;left:-100px;top:-100px;width:1px;height:1px;opacity:0;pointer-events:none;';
    const input = document.createElement('input');
    input.type = 'checkbox';
    input.setAttribute('switch', ''); // iOS 17.4+ native switch → light haptic on toggle
    label.appendChild(input);
    document.body.appendChild(label);
    hapticSwitch = label;
  }

  function ensure(): AudioContext | null {
    if (ctx) return ctx;
    const Ctor = window.AudioContext;
    if (!Ctor) return null;
    ctx = new Ctor();
    master = ctx.createGain();
    master.gain.value = 0.55;
    // Master GLUE-LIMITER: catch peaks when SFX + music stack so the mix never clips, while leaving
    // headroom below (gain-staging best-practice). Fast attack + short release = transparent control.
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -10; // start gentle gain reduction here
    comp.knee.value = 8;
    comp.ratio.value = 12; // limiter-ish above threshold
    comp.attack.value = 0.003;
    comp.release.value = 0.25;
    master.connect(comp);
    comp.connect(ctx.destination);
    // SFX-only sub-bus → master (music routes straight to master, so this volume is SFX-independent).
    sfxGain = ctx.createGain();
    sfxGain.gain.value = sfxVolume;
    sfxGain.connect(master);
    const len = Math.floor(ctx.sampleRate * 1.6);
    noiseBuf = ctx.createBuffer(1, len, ctx.sampleRate);
    const data = noiseBuf.getChannelData(0);
    const rand = createRng(0xa17d_10); // white-noise source (audio only; not gameplay)
    for (let i = 0; i < len; i++) data[i] = rand() * 2 - 1;
    return ctx;
  }

  function tone(at: number, freq: number, dur: number, opts: ToneOpts = {}): void {
    if (!ctx || !master) return;
    const osc = ctx.createOscillator();
    osc.type = opts.type ?? 'sine';
    osc.frequency.setValueAtTime(snapToKey(freq), at); // keep every pitched cue in the music's key
    if (opts.sweepTo) osc.frequency.exponentialRampToValueAtTime(Math.max(20, snapToKey(opts.sweepTo)), at + dur);
    const g = ctx.createGain();
    const peak = opts.gain ?? 0.2;
    g.gain.setValueAtTime(0.0001, at);
    g.gain.exponentialRampToValueAtTime(peak, at + 0.006);
    g.gain.exponentialRampToValueAtTime(0.0001, at + dur);
    osc.connect(g);
    g.connect(sfxGain ?? master);
    osc.start(at);
    osc.stop(at + dur + 0.03);
  }

  function noise(at: number, dur: number, freq: number, q: number, gain: number, type: BiquadFilterType = 'bandpass'): void {
    if (!ctx || !master || !noiseBuf) return;
    const src = ctx.createBufferSource();
    src.buffer = noiseBuf;
    const filt = ctx.createBiquadFilter();
    filt.type = type;
    filt.frequency.value = freq;
    filt.Q.value = q;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, at);
    g.gain.exponentialRampToValueAtTime(gain, at + Math.min(0.04, dur * 0.3));
    g.gain.exponentialRampToValueAtTime(0.0001, at + dur);
    src.connect(filt);
    filt.connect(g);
    g.connect(sfxGain ?? master);
    src.start(at);
    src.stop(at + dur + 0.03);
  }

  /** Noise through a bandpass whose frequency RISES — the "raahh" swell of a crowd. */
  function noiseSweep(at: number, dur: number, f0: number, f1: number, q: number, gain: number): void {
    if (!ctx || !master || !noiseBuf) return;
    const src = ctx.createBufferSource();
    src.buffer = noiseBuf;
    const filt = ctx.createBiquadFilter();
    filt.type = 'bandpass';
    filt.Q.value = q;
    filt.frequency.setValueAtTime(f0, at);
    filt.frequency.linearRampToValueAtTime(f1, at + dur * 0.6);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, at);
    g.gain.exponentialRampToValueAtTime(gain, at + dur * 0.25);
    g.gain.exponentialRampToValueAtTime(0.0001, at + dur);
    src.connect(filt);
    filt.connect(g);
    g.connect(sfxGain ?? master);
    src.start(at);
    src.stop(at + dur + 0.03);
  }

  function cheer(at: number, intensity: number): void {
    // A crowd ROAR, scaled by intensity (birdie < eagle < ace): a low body, a rising
    // mid "raahh", and a high applause sparkle — layered so it reads as people, not wind.
    const g = 0.2 + intensity * 0.3;
    const dur = 0.7 + intensity * 1.1;
    noise(at, dur, 480, 0.5, g * 0.7, 'lowpass'); // crowd body
    noiseSweep(at, dur, 700, 1500, 0.6, g); // the rising roar
    noise(at + 0.04, dur * 0.9, 3500, 1.1, g * 0.45, 'highpass'); // applause sparkle
  }

  /** Birdie: a quick flutter of rising chirps. */
  function chirp(at: number): void {
    for (let i = 0; i < 4; i++) {
      const f = 2600 + i * 260;
      tone(at + i * 0.085, f, 0.05, { type: 'sine', gain: 0.07, sweepTo: f * 1.4 });
    }
  }

  /** Eagle: a confident descending raptor screech (raspy sawtooth + noise). */
  function screech(at: number): void {
    tone(at, 1850, 0.42, { type: 'sawtooth', gain: 0.12, sweepTo: 820 });
    noise(at, 0.4, 1500, 1.4, 0.05);
  }

  /** The Gopher's cheeky cackle — quick squeaky notes that bounce in pitch ("hee-hee-hee-hee"). */
  function gopherLaugh(at: number): void {
    const base = 780;
    for (let i = 0; i < 5; i++) {
      const t = at + i * 0.1;
      const f = base + (i % 2 === 0 ? 200 : 0);
      tone(t, f, 0.07, { type: 'square', gain: 0.17, sweepTo: f * 1.3 });
      tone(t, f * 2, 0.05, { type: 'sine', gain: 0.045 }); // a little sparkle on top
    }
    tone(at + 0.54, 600, 0.14, { type: 'triangle', gain: 0.13, sweepTo: 470 }); // chuckle tail
  }

  function scored(at: number, result: ScoreResult): void {
    switch (result) {
      case 'ace':
        tone(at, 660, 0.16, { type: 'triangle', gain: 0.26 });
        tone(at + 0.12, 880, 0.16, { type: 'triangle', gain: 0.26 });
        tone(at + 0.24, 1320, 0.32, { type: 'triangle', gain: 0.28 });
        cheer(at + 0.18, 1.0); // biggest roar
        break;
      case 'eagle':
        tone(at, 660, 0.16, { type: 'triangle', gain: 0.24 });
        tone(at + 0.12, 990, 0.28, { type: 'triangle', gain: 0.26 });
        cheer(at + 0.1, 0.75);
        screech(at + 0.16); // the eagle's confident cry
        break;
      case 'birdie':
        tone(at, 880, 0.3, { type: 'sine', gain: 0.24 });
        tone(at, 1320, 0.3, { type: 'sine', gain: 0.1 });
        cheer(at + 0.08, 0.42); // a smaller cheer
        chirp(at + 0.12); // little birds
        break;
      case 'par':
        tone(at, 520, 0.16, { type: 'sine', gain: 0.18 });
        break;
      case 'bogey':
        tone(at, 330, 0.22, { type: 'sine', gain: 0.18, sweepTo: 300 });
        break;
      case 'double':
        tone(at, 300, 0.34, { type: 'sine', gain: 0.18, sweepTo: 190 });
        break;
    }
  }

  function buzz(pattern: number | number[]): void {
    if (muted || !hapticsEnabled) return;
    // Native shell: real Taptic/vibration — and nothing below (no web fallbacks needed).
    if (NATIVE) {
      nativeBuzz(pattern);
      return;
    }
    // Android (+ most non-iOS) web: the Vibration API honours the full pattern.
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') navigator.vibrate(pattern);
    // iOS: only a light tick is possible — toggle the switch once per pulse in the pattern.
    if (hapticSwitch) {
      const pulses = Array.isArray(pattern) ? Math.max(1, Math.ceil(pattern.length / 2)) : 1;
      const el = hapticSwitch;
      for (let i = 0; i < pulses; i++) setTimeout(() => el.click(), i * 70);
    }
  }

  function play(e: GameEvent): void {
    if (!ctx) return;
    const at = ctx.currentTime;
    switch (e.kind) {
      case 'backswingLock':
        tone(at, 1000, 0.04, { type: 'square', gain: 0.08 });
        buzz(8);
        break;
      case 'strike': {
        const g = e.mine ? 1 : 0.55;
        noise(at, 0.11, 1300, 0.7, 0.13 * g); // swing whoosh
        tone(at + 0.02, 380, 0.08, { type: 'triangle', gain: 0.26 * g }); // club "tock"
        noise(at + 0.02, 0.03, 4000, 0.9, 0.1 * g, 'highpass'); // crisp contact click
        if (e.mine) buzz(16);
        break;
      }
      case 'land':
        tone(at, 175, 0.12, { type: 'sine', gain: (e.mine ? 0.18 : 0.1) });
        noise(at, 0.1, 300, 0.6, (e.mine ? 0.08 : 0.04), 'lowpass');
        break;
      case 'water':
        tone(at, 520, 0.26, { type: 'sine', gain: 0.18, sweepTo: 150 });
        noise(at + 0.02, 0.3, 800, 0.7, 0.12);
        if (e.mine) buzz(22);
        break;
      case 'scored':
        scored(at, e.result);
        if (e.mine) {
          if (e.result === 'ace') buzz([28, 40, 28, 40, 60]);
          else if (e.result === 'eagle' || e.result === 'birdie') buzz([14, 30, 14]);
          else if (e.result === 'double' || e.result === 'bogey') buzz(30);
        }
        break;
      case 'gopher':
        gopherLaugh(at + 0.12); // lands on the snatch
        if (e.mine) buzz([18, 50, 18]); // your ball got nabbed
        break;
    }
  }

  return {
    unlock(): void {
      ensureHaptic();
      // iOS mutes Web Audio when the ringer switch is on SILENT unless the page declares
      // its audio as "playback" (iOS 16.4+). Without this you hear nothing on silent.
      try {
        const sess = (navigator as unknown as { audioSession?: { type: string } }).audioSession;
        if (sess) sess.type = 'playback';
      } catch {
        /* Audio Session API not supported — ignore. */
      }
      const c = ensure();
      if (c && c.state === 'suspended') void c.resume();
    },
    emit(events): void {
      if (muted || events.length === 0) return;
      const c = ensure();
      if (!c) return;
      if (c.state === 'suspended') void c.resume();
      for (const e of events) play(e);
    },
    setMuted(m): void {
      muted = m;
      if (master && ctx) master.gain.setTargetAtTime(m ? 0 : 0.55, ctx.currentTime, 0.02);
    },
    setSfxVolume(v): void {
      sfxVolume = v < 0 ? 0 : v > 1 ? 1 : v;
      if (sfxGain && ctx) sfxGain.gain.setTargetAtTime(sfxVolume, ctx.currentTime, 0.02);
    },
    setHaptics(on): void {
      hapticsEnabled = on;
    },
    audioGraph(): AudioGraph | null {
      const c = ensure();
      if (!c || !master) return null;
      return { ctx: c, master };
    },
  };
}
